import {ReleaseReadyControl} from '../components/ReleaseReadyControl'
import {ownerDecision} from '../lib/releaseOwnerDecision'
import {ReleaseCheckControl} from '../components/ReleaseCheckControl'
import { ReleaseTaskStatus } from '../components/ReleaseTaskStatus'
import { RefreshCw } from "lucide-react";
import toast from "react-hot-toast";
import { queueErrorMessage } from "@/lib/queueBreakGlass";
import { BulkWorkflowOwnerControl } from "@/components/BulkWorkflowOwnerControl";
import {
  bulkSelectionReason,
  type BulkTask,
  bulkTaskIdentity,
} from "@/lib/bulkWorkflowOwner";
import { ownerApprovalFeedback, packageHeads } from "@/lib/workflowOwner";
import { migrationQueueApi } from "@/api/migrationQueue";
import { readJumpstartQueueSearchState } from "@/api/queueSearchState";
import {
  isQueueHistory,
  prepareQueueSearch,
  queueResultCount,
  queueStatusLine,
  searchQueueItems,
} from "@/lib/queueSearch";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  type ReleasePr,
  releasesApi,
  type ReleasesSnapshot,
} from "@/api/releases";
import { WorkflowOwnerControl } from "@/components/WorkflowOwnerControl";
import { PrTaskLink } from "@/components/PrTaskLink";
import { ReleasePrDetails } from "@/components/ReleasePrDetails";
import { ReleaseControlPanel } from "@/components/ReleaseControlPanel";
import {
  nightReleaseState,
  releaseChecks,
  releaseGroups,
  releaseReason,
  releaseStatus,
  whatChanges,
} from "@/lib/releaseCenter";
import { sharedPoll, stopPoll } from "@/command/poll";
import { type QueueItem } from "@/api/migrationQueue";
import { adminMigrationQueueApi } from "@/api/adminMigrationQueue";
import {
  parsePrSearch,
  prSearchRank,
  rankPrResults,
} from "@/lib/releaseSearch";
import "@/command/release-center.css";

type SqlRow = QueueItem & { database: "admin" | "jumpstart" };
type SearchTask = {
  id: string;
  title: string;
  status?: string;
  context?: string | null;
};
type Replaced = {
  repo: string;
  number: number;
  head_sha: string;
  title: string;
  replacement_url: string;
  evidence: string;
};
const labels = {
  tonight: "ייצא הלילה",
  waiting: "מחכה לך",
  stuck: "תקוע",
  agents: "אצל הסוכנים",
  old: "ישן",
};

function CompactRow(
  {
    id,
    title,
    level,
    status,
    checks,
    children,
    autoOpenKey,
    subtitle,
    selection,
    selectionReason,
    taskControl,
  }: {
    id: string;
    title: string;
    autoOpenKey?: string;
    subtitle?: ReactNode;
    selection?: ReactNode;
    taskControl?: ReactNode;
    selectionReason?: string;
    level?: number | null;
    status?: string;
    checks?: "passed" | "failed" | "unknown";
    children: ReactNode;
  },
) {
  const panelId = useId();
  const storageKey = "release-center:fix01:expanded:" + id;
  const [expanded, setExpanded] = useState(() => {
    try {
      return Boolean(autoOpenKey) ||
        sessionStorage.getItem(storageKey) === "true";
    } catch {
      return Boolean(autoOpenKey);
    }
  });
  useEffect(() => {
    if (autoOpenKey) setExpanded(true);
  }, [autoOpenKey]);
  function toggle() {
    const next = !expanded;
    setExpanded(next);
    try {
      sessionStorage.setItem(storageKey, String(next));
    } catch { /* private browsing still supports disclosure */ }
  }
  const checkLabel = checks === "passed"
    ? "הבדיקות תקינות"
    : checks === "failed"
    ? "נדרש טיפול בבדיקות"
    : "הבדיקות טרם אומתו";
  return (
    <article
      className="rc-card rc-compact"
      data-expanded={expanded}
      data-selectable={Boolean(selection)}
    >
      {selection}

      <button
        className="rc-summary"
        onClick={toggle}
        aria-expanded={expanded}
        aria-controls={panelId}
      >
        <span className="rc-title-wrap">
          <span className="rc-title" title={title}>{title}</span>
          {subtitle && <small className="rc-subtitle">{subtitle}</small>}
        </span>
        {level !== undefined && (
          <span className="rc-chip">
            {level === null ? "רמה לא אומתה" : <bdi>L{level}</bdi>}
          </span>
        )}
        {status && <span className="rc-status">{status}</span>}
        {checks && (
          <span
            className="rc-check"
            data-checks={checks}
            role="img"
            aria-label={checkLabel}
            title={checkLabel}
          >
            {checks === "passed" ? "✓" : checks === "failed" ? "!" : "?"}
          </span>
        )}
        <span className="rc-chevron" aria-hidden="true">
          {expanded ? "−" : "+"}
        </span>
      </button>
      {taskControl}
      {selectionReason && (
        <p
          id={`choice-${id.replace(/^task:/, "")}`}
          className="rc-selection-reason"
        >
          {selectionReason}
        </p>
      )}
      <div id={panelId} hidden={!expanded} className="rc-details">
        {expanded && children}
      </div>
    </article>
  );
}

function Counts({ prs }: { prs: ReleasePr[] }) {
  const sql = prs.reduce((sum, pr) => sum + (pr.migrations?.length ?? 0), 0);
  return (
    <p className="rc-counts">
      <span>
        {prs.length === 1 ? "PR אחד" : (
          <>
            <bdi>{prs.length}</bdi> שינויים (PR)
          </>
        )}
      </span>
      <span aria-hidden="true">·</span>
      <span>
        {sql === 0 ? "ללא SQL" : sql === 1 ? "שינוי SQL אחד" : (
          <>
            <bdi>{sql}</bdi> שינויי SQL
          </>
        )}
      </span>
    </p>
  );
}

function TaskDecisions({taskId,all,owner,sql,refreshSql}:{taskId:string;all:ReleasePr[];owner:boolean;sql:SqlRow[];refreshSql:()=>Promise<void>}){
 const pr=all.find(p=>p.task?.id===taskId&&p.state==='open')
 return pr?<WorkflowOwnerControl owner={owner} pr={pr} all={all} sqlControls={sql.filter(row=>row.task_id===taskId||row.pr_url===pr.url).map(row=><SqlCard key={row.database+row.id} row={row} owner={owner} refresh={refreshSql}/>)}/>:<div className="rc-decision"><div className="rc-decision-buttons"><button disabled>מיזוג הלילה</button><button disabled>מיזוג עכשיו</button></div><p>אין PR פתוח המקושר למשימה.</p></div>
}

function PrCard({
  pr,
  all,
  owner,
  refresh,
  autoOpenKey,
  sqlRows = [],
  refreshSql,
}: {
  pr: ReleasePr;
  sqlRows?: SqlRow[];
  refreshSql?: () => Promise<void>;
  all: ReleasePr[];
  owner: boolean;
  dayAllowed?: boolean;
  autoOpenKey?: string;
  loading?: boolean;
  refresh: () => Promise<void>;
}) {
  const [note, setNote] = useState(""),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState("");
  const canAct = owner && !releasesApi.workflowReadOnly && pr.state === "open";
  async function act(kind: "comment" | "fix" | "close") {
    if (busy || !canAct) return;
    if (
      kind === "close" &&
      !window.confirm(`לסגור ללא מיזוג את ${pr.repo} #${pr.number}?`)
    ) {
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      const response = await releasesApi.centerAction(pr, kind, note);
      setMessage(response.message);
      if (kind === "close") await refresh();
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const preview = pr.control_snapshot?.preview_url;
  return (
    <CompactRow
      id={pr.url}
      autoOpenKey={autoOpenKey}
      subtitle={<bdi>{pr.repo.replace("the-Otomator/", "")} #{pr.number}</bdi>}
      title={pr.task?.title ?? pr.title}
      level={pr.level}
      status={releaseStatus(pr)}
      checks={releaseChecks(pr)}
      taskControl={<WorkflowOwnerControl owner={owner} pr={pr} all={all} sqlControls={refreshSql&&sqlRows.map(row=><SqlCard key={row.database+row.id} row={row} owner={owner} linkedPr={pr} refresh={refreshSql}/>)}/>}
    >
      <div className="rc-row">
        <a href={pr.url} target="_blank" rel="noreferrer">
          <bdi>
            {pr.repo.replace("the-Otomator/", "")} #{pr.number}
          </bdi>
        </a>
        <span className="rc-chip">
          {pr.level === null
            ? "רמה טרם אומתה"
            : pr.level <= 1
            ? "מסכים בלבד"
            : `רמה ${pr.level}`}
        </span>
      </div>
      <h3>{pr.title}</h3>
      {pr.task && <ReleaseTaskStatus task={pr.task} owner={owner} all={all} />}
      {pr.task?.context && <p>{pr.task.context}</p>}
      {whatChanges(pr) && <p>מה משתנה: {whatChanges(pr)}</p>}
      <p className="rc-reason" data-approved={Boolean(ownerDecision(pr))}>{releaseReason(pr)}</p>
      <ReleaseReadyControl pr={pr} owner={owner} refresh={refresh}/>
      <ReleaseCheckControl pr={pr} owner={owner}/>
      {(pr.state === "open" || pr.task) && (
        <PrTaskLink pr={pr} refresh={refresh} />
      )}
      <div className="rc-actions">
        {typeof preview === "string" && /^https:\/\//.test(preview) && (
          <a
            className="rc-button"
            href={preview}
            target="_blank"
            rel="noreferrer"
          >
            תצוגה מקדימה
          </a>
        )}
        {pr.state === "open" ? <ReleasePrDetails pr={pr} /> : (
          <a
            className="rc-button"
            href={pr.url + "/files"}
            target="_blank"
            rel="noreferrer"
          >
            מה השתנה ב־PR
          </a>
        )}
        <button disabled={!canAct || busy} onClick={() => void act("close")}>
          לסגור PR
        </button>
      </div>
      <label>
        הערה לסוכן
        <textarea
          rows={2}
          maxLength={2000}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="מה צריך לתקן באותו PR?"
        />
      </label>
      <div className="rc-actions">
        <button
          disabled={!canAct || busy || note.trim().length < 5}
          onClick={() => void act("comment")}
        >
          להגיב
        </button>
        <button
          disabled={!canAct || busy || note.trim().length < 5}
          onClick={() => void act("fix")}
        >
          לשלוח לסוכן לתיקון באותו PR
        </button>
      </div>
      {message && <p role="status">{message}</p>}
      {refreshSql && sqlRows.map((row) => <SqlCard key={row.database + row.id} row={row} owner={owner} linkedPr={pr} refresh={refreshSql} />)}
      {all.length === 0 && <p>אין נתוני חבילה זמינים</p>}
    </CompactRow>
  );
}

export default function ReleaseCenterPage() {
  const [selected, setSelected] = useState<Record<string, string>>({});
  const [params, setParams] = useSearchParams(),
    [query, setQuery] = useState(params.get("search") ?? ""),
    [filter, setFilter] = useState("all"),
    [openOnly, setOpenOnly] = useState(false),
    [section, setSection] = useState<keyof typeof labels>("waiting");
  const main = useRef<HTMLElement>(null),
    scrollHost = useRef<HTMLElement | null>(null);
  const [showTop, setShowTop] = useState(false);
  useEffect(() => {
    let parent = main.current?.parentElement ?? null;
    while (
      parent && !/(auto|scroll)/.test(getComputedStyle(parent).overflowY)
    ) parent = parent.parentElement;
    const host = parent ?? document.scrollingElement as HTMLElement;
    scrollHost.current = host;
    const target = parent ?? window;
    const update = () =>
      setShowTop(
        host.scrollTop >= (parent ? host.clientHeight : window.innerHeight),
      );
    target.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    update();
    return () => {
      target.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);
  function top() {
    scrollHost.current?.scrollTo({ top: 0, behavior: "auto" });
  }

  const [snapshot, setSnapshot] = useState<ReleasesSnapshot | null>(null),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(true);
  const [sql, setSql] = useState<SqlRow[]>([]),
    [sqlError, setSqlError] = useState(""),
    [sqlLoading, setSqlLoading] = useState(true),
    [searchPrs, setSearchPrs] = useState<ReleasePr[] | null>(null),
    [tasks, setTasks] = useState<SearchTask[]>([]),
    [searchError, setSearchError] = useState(""),
    [searchLoading, setSearchLoading] = useState(false);
  const [control, setControl] = useState<
      Awaited<
        ReturnType<typeof releasesApi.control>
      > | null
    >(null),
    [controls, setControls] = useState(false),
    [emergency, setEmergency] = useState(false);
  const [replaced, setReplaced] = useState<Replaced[] | null>(null),
    [cleanupMessage, setCleanupMessage] = useState(""),
    [cleanupBusy, setCleanupBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      setSnapshot(await releasesApi.list());
      setError("");
      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    const timer = sharedPoll(load, 30000, true);
    return () => stopPoll(timer);
  }, [load]);
  useEffect(() => {
    let active = true;
    const timer = sharedPoll(
      async () => {
        try {
          const data = await releasesApi.control("get");
          if (active) setControl(data);
          return true;
        } catch {
          if (active) setControl(null);
          return false;
        }
      },
      30000,
      true,
    );
    return () => {
      active = false;
      stopPoll(timer);
    };
  }, []);
  useEffect(()=>{const refresh=()=>void load();window.addEventListener('release-task-status',refresh);window.addEventListener('release-task-change',refresh);return()=>{window.removeEventListener('release-task-status',refresh);window.removeEventListener('release-task-change',refresh)}},[load]);
  const sqlGeneration = useRef(0);
  const loadSql = useCallback(async () => {
    const generation = ++sqlGeneration.current;
    const results = await Promise.allSettled([
      readJumpstartQueueSearchState(), adminMigrationQueueApi.state(),
    ]);
    try {
      const next = await Promise.all(results.flatMap((r, i) => r.status === "fulfilled"
        ? r.value.items.map(async (row) => ({ ...await prepareQueueSearch(row),
          database: i === 0 ? "jumpstart" as const : "admin" as const })) : []));
      if (generation !== sqlGeneration.current) return;
      setSql(next);
      if (results.some((r) => r.status === "rejected")) throw Error("אחד מתורי SQL אינו זמין; החיפוש חלקי.");
      setSqlError("");
    } catch (error) {
      if (generation !== sqlGeneration.current) return;
      setSql([]);
      const message = queueErrorMessage(error);
      setSqlError(message);
      throw Error(message);
    } finally { if (generation === sqlGeneration.current) setSqlLoading(false); }
  }, []);
  useEffect(() => {
    const timer = sharedPoll(async () => {
      try { await loadSql(); return true; } catch { return false; }
    }, 30000, true);
    return () => { ++sqlGeneration.current; stopPoll(timer); };
  }, [loadSql]);
  useEffect(() => {
    let active = true;
    const timer = setTimeout(() => {
      if (!query.trim() && filter === "all") {
        setTasks([]);
        setSearchPrs(null);
        setSearchError("");
        setSearchLoading(false);
        return;
      }
      setSearchLoading(true);
      void releasesApi
        .centerSearch(
          query,
          openOnly,
          snapshot?.prs ?? [],
          (snapshot?.repos ?? []).map((repo) => repo.repo),
        )
        .then((data) => {
          if (active) {
            setTasks(data.tasks);
            setSearchPrs("prs" in data ? (data.prs ?? null) : null);
            setSearchError(data.warning ?? "");
          }
        })
        .catch(() => {
          if (active) {
            setTasks([]);
            setSearchPrs(null);
            setSearchError(
              "חלק ממקורות החיפוש אינם זמינים כרגע; הרשימה עשויה להיות חלקית.",
            );
          }
        }).finally(() => {
          if (active) setSearchLoading(false);
        });
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [query, openOnly, filter, snapshot]);
  const groups = releaseGroups(snapshot?.prs ?? []),
    owner = snapshot?.viewer_is_nizan === true;
  const choices = groups.filter((group) => group.bucket === "waiting").map(
    (group) => ({
      task: {
        id: group.id,
        title: group.title,
        heads: packageHeads(snapshot?.prs ?? [], group.prs[0]),
      } as BulkTask,
      reason: bulkSelectionReason(group.prs),
    }),
  );
  const choiceKey = JSON.stringify(
    choices.map((choice) => [bulkTaskIdentity(choice.task), choice.reason]),
  );
  useEffect(() => {
    setSelected((old) => {
      const next = Object.fromEntries(
        Object.entries(old).filter(([id, key]) =>
          choices.some((choice) =>
            choice.task.id === id && !choice.reason &&
            bulkTaskIdentity(choice.task) === key
          )
        ),
      );
      return JSON.stringify(old) === JSON.stringify(next) ? old : next;
    });
  }, [choiceKey]);
  const eligible = choices.filter((choice) => !choice.reason);
  const selectedTasks = eligible.filter((choice) =>
    selected[choice.task.id] === bulkTaskIdentity(choice.task)
  ).map((choice) => choice.task);
  const prs = rankPrResults(
    query,
    searchPrs ?? snapshot?.prs ?? [],
    tasks.map((task) => task.id),
  ).filter((pr) => !openOnly || pr.state === "open");
  const sqlResults = searchQueueItems(sql, query);
  const nightly = nightReleaseState(control, error ? null : snapshot);
  const dayAllowed = (rows: ReleasePr[]) =>
    rows.length > 0 &&
    rows.every(
      (p) =>
        p.level !== null &&
        p.level <= 1 &&
        snapshot?.repos.find((r) => r.repo === p.repo)?.day_merge_enabled ===
          true,
    ) &&
    snapshot?.repos.find((r) => r.repo === "*")?.day_merge_enabled === true;
  const last = snapshot?.runs?.[0];
  const oldGroups = groups.filter((group) =>
    group.prs.some((pr) =>
      pr.gh_updated_at &&
      Date.now() - Date.parse(pr.gh_updated_at) >= 14 * 86400000
    )
  );
  const visibleGroups = section === "old"
    ? oldGroups
    : groups.filter((group) => group.bucket === section);
  const searching = query.trim().length > 0 || filter !== "all";
  function search(value: string) {
    setQuery(value);
    const next = new URLSearchParams(params);
    if (value) next.set("search", value);
    else next.delete("search");
    setParams(next, { replace: true });
  }
  async function cleanup(close = false) {
    if (cleanupBusy) return;
    if (
      close &&
      !window.confirm(
        `לסגור רק את ${
          replaced?.length ?? 0
        } ה־PR המוחלפים ברשימה? הפעולה תיבדק שוב מול GitHub.`,
      )
    ) {
      return;
    }
    setCleanupBusy(true);
    setCleanupMessage("");
    try {
      if (close) {
        const result = await releasesApi.closeReplaced(replaced ?? []);
        setCleanupMessage(result.message);
        setReplaced(null);
        await load();
      } else setReplaced((await releasesApi.replaced()).prs);
    } catch (e) {
      setCleanupMessage((e as Error).message);
    } finally {
      setCleanupBusy(false);
    }
  }
  return (
    <main ref={main} className="release-center" dir="rtl" lang="he">
      {nightly !== "on" && (
        <aside className="rc-banner" role="status">
          <strong>
            {nightly === "unknown"
              ? "מצב השחרור הלילי טרם אומת"
              : "השחרור הלילי כבוי"}
          </strong>
          <span>
            {nightly === "unknown"
              ? "לא ניתן לאמת את מצב הבקר כרגע. רעננו לפני אישור שחרור."
              : nightly === "paused"
              ? "הבקר מושהה זמנית; השחרור יישאר כבוי עד לסיום ההשהיה ואימות המוכנות."
              : "מה שתאשר לא ייצא עד שהבקר יופעל. חלון לילה: 23:00–06:00 · הריצה המתוכננת ב־23:15."}
          </span>
          <button onClick={() => setControls(!controls)}>
            הגדרות השחרור הלילי
          </button>
        </aside>
      )}
      <div className="rc-sticky">
        <header className="rc-header">
          <div className="rc-page-title">
            <small>מרכז הפיקוד</small>
            <h1>שחרורים</h1>
          </div>
          <div className="rc-header-actions">
            <input
              type="search"
              aria-label="חיפוש בשחרורים"
              placeholder="חיפוש PR, SQL, משימה או לקוח"
              value={query}
              onChange={(e) => search(e.target.value)}
            />
            <button
              className="rc-danger rc-emergency-toggle"
              onClick={() => setEmergency(!emergency)}
            >
              שחרור חירום
            </button>
            <button className="rc-refresh" aria-label="רענון" title="רענון" onClick={() => void load()} disabled={loading}>
              <RefreshCw className="rc-refresh-icon" size={18} aria-hidden="true" />
              <span className="rc-refresh-label">רענון</span>
            </button>
          </div>
        </header>
        <nav className="rc-strip" role="tablist" aria-label="מצב שחרורים">
          {(Object.keys(labels) as (keyof typeof labels)[]).map((key) => (
            <button
              key={key}
              id={`rc-tab-${key}`}
              role="tab"
              aria-selected={!searching && section === key}
              aria-controls={searching ? "rc-search-results" : "rc-section"}
              data-tone={key}
              onClick={() => {
                setSection(key);
                search("");
                setFilter("all");
                setOpenOnly(false);
                top();
              }}
            >
              <b>
                <bdi>
                  {(key === "old"
                    ? oldGroups
                    : groups.filter((g) => g.bucket === key)).length}
                </bdi>
              </b>
              <span>{labels[key]}</span>
            </button>
          ))}
        </nav>
        <nav className="rc-filters" aria-label="סינון חיפוש">
          {[
            ["all", "הכל"],
            ["pr", "PR"],
            ["sql", "SQL"],
            ["task", "משימות"],
          ].map(([key, label]) => (
            <button
              key={key}
              aria-pressed={filter === key}
              onClick={() => setFilter(key)}
            >
              {label}
            </button>
          ))}
          <button
            aria-pressed={openOnly}
            onClick={() => setOpenOnly(!openOnly)}
          >
            פתוחים בלבד
          </button>
          {searching && (
            <button
              className="rc-clear-search"
              data-has-query={Boolean(query.trim())}
              onClick={() => {
                search("");
                setFilter("all");
                setOpenOnly(false);
              }}
            >
              ניקוי חיפוש
            </button>
          )}
        </nav>
      </div>
      {controls && <ReleaseControlPanel canEdit={owner} />}
      {emergency && (
        <aside className="rc-card">
          <h2>שחרור חירום</h2>
          <p>
            בחרו משימה. מסלול החירום מציג את כל ה־PR וחתימות SQL ודורש אישור
            בעלים מפורש.
          </p>
          {groups
            .filter((g) => g.prs[0].task)
            .map((g) => (
              <WorkflowOwnerControl
                key={g.id}
                owner={owner}
                pr={g.prs[0]}
                all={snapshot?.prs}
                initialMode="emergency"
              />
            ))}
        </aside>
      )}
      {error && (
        <p role="alert" className="rc-error">
          {error}
        </p>
      )}
      {loading && !snapshot && <p role="status">טוען שחרורים…</p>}
      {searching
        ? (
          <section
            id="rc-search-results"
            className="rc-search"
            aria-label="תוצאות חיפוש"
          >
            {searchLoading && <p role="status">מחפש PR ומיגרציות…</p>}
            {searchError && <p role="alert">{searchError}</p>}
            {(filter === "all" || filter === "pr") &&
              prs.map((pr, index) => (
                <PrCard
                  key={pr.url}
                  autoOpenKey={index === 0 && parsePrSearch(query) &&
                      prSearchRank(query, pr) === 100
                    ? query
                    : undefined}
                  pr={pr}
                  sqlRows={sql.filter((row) => row.pr_url === pr.url)}
                  refreshSql={loadSql}
                  all={[...(snapshot?.prs??[]),...(snapshot?.workflow_executions??[]),...(searchPrs??[])]}
                  owner={owner}
                  loading={!snapshot && loading}
                  dayAllowed={dayAllowed(
                    (snapshot?.prs ?? []).filter(
                      (p) => Boolean(pr.task) && p.task?.id === pr.task?.id,
                    ),
                  )}
                  refresh={async () => {
                    await load();
                  }}
                />
              ))}
            {(filter === "all" || filter === "sql") && (
              <>
                {sqlError && <p role="alert">{sqlError}</p>}
                {sqlLoading && <p role="status">טוען את שני תורי SQL…</p>}
                <p role="status">{queueResultCount(sqlResults)}</p>
                {sqlResults.map((row) => (
                  <SqlCard
                    key={row.database + row.id}
                    row={row}
                    refresh={loadSql}
                    linkedPr={(snapshot?.prs ?? []).find((pr) =>
                      pr.url === row.pr_url
                    )}
                    owner={owner}
                  />
                ))}
              </>
            )}
            {(filter === "all" || filter === "task") && (
              <>
                {tasks.map((task) => (
                  <CompactRow
                    key={task.id}
                    id={"search-task:" + task.id}
                    taskControl={<><ReleaseTaskStatus task={task} owner={owner} all={[...(snapshot?.prs??[]),...(snapshot?.workflow_executions??[]),...(searchPrs??[])]} /><TaskDecisions taskId={task.id} all={[...(snapshot?.prs??[]),...(searchPrs??[])]} owner={owner} sql={sql} refreshSql={loadSql}/></>}
                    title={task.title}
                  >
                    <small>משימה</small>
                    <h3>{task.title}</h3>
                    {task.context && <p>{task.context}</p>}
                    <a
                      href={`https://hub.jumpstart.co.il/tasks/table?task=${
                        encodeURIComponent(task.id)
                      }`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      לפתוח את המשימה
                    </a>
                    {sql.filter((row) => row.task_id === task.id).map((row) => <SqlCard key={row.database + row.id} row={row} owner={owner} refresh={loadSql} />)}
                  </CompactRow>
                ))}
              </>
            )}
            {!searchLoading && !sqlLoading &&
              !(filter === "all" || filter === "pr" ? prs.length : 0) &&
              !(filter === "all" || filter === "sql" ? sqlResults.length : 0) &&
              !(filter === "all" || filter === "task" ? tasks.length : 0) && (
              <p className="rc-empty">
                לא נמצא PR או מיגרציה עבור ״{query || "הסינון שנבחר"}״. נסו מספר
                PR, קישור GitHub, שם קובץ SQL, תחילת גרסה או חתימת תוכן. לחיפוש
                בהיסטוריה ודאו ש״פתוחים בלבד״ כבוי.
              </p>
            )}
          </section>
        )
        : (
          <section
            id="rc-section"
            className="rc-section"
            role="tabpanel"
            aria-labelledby={`rc-tab-${section}`}
          >
            <h2>
              {labels[section]}
              {section === "waiting" ? " · לפי משימה" : ""}
            </h2>
            {section === "old" && (
              <p>
                משימות עם PR שלא עודכן מעל 14 יום. גיל לבדו אינו סיבה לסגירה.
              </p>
            )}
            {!visibleGroups.length && (
              <p className="rc-empty">אין משימות בקטגוריה</p>
            )}
            {section === "waiting" && (
              <div className="rc-bulk-toolbar">
                <label className="rc-bulk-select-all">
                  <input
                    type="checkbox"
                    aria-label="בחר הכל"
                    disabled={!owner || !eligible.length}
                    checked={eligible.length > 0 &&
                      selectedTasks.length === eligible.length}
                    onChange={(event) =>
                      setSelected(
                        event.target.checked
                          ? Object.fromEntries(
                            eligible.map(
                              (choice) => [
                                choice.task.id,
                                bulkTaskIdentity(choice.task),
                              ],
                            ),
                          )
                          : {},
                      )}
                  />בחר הכל
                </label>
                <span>{selectedTasks.length} משימות נבחרו</span>
                <BulkWorkflowOwnerControl
                  tasks={selectedTasks}
                  all={[...(snapshot?.prs??[]),...(snapshot?.workflow_executions??[]),...(searchPrs??[])]}
                  owner={owner}
                  onFinished={() => {
                    setSelected({});
                    void load();
                  }}
                />
              </div>
            )}
            {visibleGroups.map((group) => {
              const rowStatus = releaseStatus(
                group.prs[0],
                section === "old" ? "old" : group.bucket,
              );
              const checks = group.prs.every((p) =>
                  releaseChecks(p) === "passed"
                )
                ? "passed"
                : group.prs.some((p) => releaseChecks(p) === "failed")
                ? "failed"
                : "unknown";
              const level = group.prs.some((p) => p.level === null)
                ? null
                : Math.max(...group.prs.map((p) => p.level!));
              return (
                <CompactRow
                  key={group.id}
                  id={"task:" + group.id}
                  taskControl={<>{group.prs.map(pr=><ReleaseCheckControl key={pr.repo+pr.number} pr={pr} owner={owner}/>)}{group.prs[0].task && <ReleaseTaskStatus task={group.prs[0].task} owner={owner} all={[...(snapshot?.prs??[]),...(snapshot?.workflow_executions??[]),...(searchPrs??[])]} />}<WorkflowOwnerControl owner={owner} pr={group.prs[0]} all={snapshot?.prs} sqlControls={sql.filter(row=>(Boolean(row.task_id)&&row.task_id===group.prs[0].task?.id)||group.prs.some(pr=>pr.url===row.pr_url)).map(row=><SqlCard key={row.database+row.id} row={row} owner={owner} refresh={loadSql}/>)}/></>}
                  title={group.title}
                  selection={section === "waiting"
                    ? (
                      <label className="rc-task-select">
                        <input
                          type="checkbox"
                          aria-label={`לבחור את ${group.title}`}
                          aria-describedby={bulkSelectionReason(group.prs)
                            ? `choice-${group.id}`
                            : undefined}
                          disabled={!owner ||
                            Boolean(bulkSelectionReason(group.prs))}
                          checked={selected[group.id] ===
                            bulkTaskIdentity({
                              id: group.id,
                              title: group.title,
                              heads: packageHeads(
                                snapshot?.prs ?? [],
                                group.prs[0],
                              ),
                            })}
                          onChange={(event) => {
                            const task = {
                              id: group.id,
                              title: group.title,
                              heads: packageHeads(
                                snapshot?.prs ?? [],
                                group.prs[0],
                              ),
                            };
                            setSelected((old) => {
                              const next = { ...old };
                              if (event.target.checked) {
                                next[group.id] = bulkTaskIdentity(task);
                              } else delete next[group.id];
                              return next;
                            });
                          }}
                        />
                      </label>
                    )
                    : undefined}
                  selectionReason={section === "waiting"
                    ? bulkSelectionReason(group.prs)
                    : undefined}
                  status={rowStatus}
                  level={level}
                  checks={checks}
                >
                  <Counts prs={group.prs} />
                  {group.prs[0].task?.context && (
                    <p>{group.prs[0].task.context}</p>
                  )}
                  {whatChanges(group.prs[0]) && (
                    <p>מה משתנה: {whatChanges(group.prs[0])}</p>
                  )}
                  <p className="rc-reason" data-approved={Boolean(ownerDecision(group.prs[0]))}>{releaseReason(group.prs[0])}</p>
                  <PrTaskLink
                    pr={group.prs[0]}
                    refresh={async () => {
                      await load();
                    }}
                  />
                  {sqlError && <p role="alert">{sqlError}</p>}
                  {sqlLoading && <p role="status">טוען את שני תורי SQL…</p>}
                  {sql.filter((row) => (Boolean(row.task_id) && row.task_id === group.prs[0].task?.id) || group.prs.some((pr) => pr.url === row.pr_url)).map((row) => (
                    <SqlCard key={row.database + row.id} row={row} owner={owner} refresh={loadSql} linkedPr={group.prs.find((pr) => pr.url === row.pr_url)} />
                  ))}
                  {group.prs.map((pr) => (
                    <PrCard
                      key={pr.url}
                      pr={pr}
                      all={[...(snapshot?.prs??[]),...(snapshot?.workflow_executions??[]),...(searchPrs??[])]}
                      owner={owner}
                      sqlRows={sql.filter(row=>row.pr_url===pr.url)}
                      refreshSql={loadSql}
                      dayAllowed={dayAllowed(group.prs)}
                      refresh={async () => {
                        await load();
                      }}
                    />
                  ))}
                </CompactRow>
              );
            })}
            {section === "old" && (
              <CompactRow
                id="old-reviewed-replacements"
                title="בדיקת PR מוחלפים לסגירה"
              >
                <h3>PR פתוחים מעל 14 יום</h3>
                <p>
                  בודקים ראיית החלפה מפורשת ב־PR חדש יותר. רק מוחלפים יוצעו
                  לסגירה.
                </p>
                <button
                  disabled={cleanupBusy}
                  onClick={() => void cleanup()}
                >
                  לבדוק את המוחלפים
                </button>
                {replaced && (
                  <>
                    <ul>
                      {replaced.map((pr) => (
                        <li key={pr.repo + pr.number}>
                          <bdi>#{pr.number}</bdi> {pr.title} ·{" "}
                          <a
                            href={pr.replacement_url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            ה־PR המחליף
                          </a>
                          <p>{pr.evidence}</p>
                        </li>
                      ))}
                    </ul>
                    {!replaced.length
                      ? <p>לא נמצאו החלפות עם ראיות מספיקות. לא ייסגר דבר.</p>
                      : (
                        <button
                          disabled={!owner || cleanupBusy}
                          onClick={() => void cleanup(true)}
                        >
                          לאשר סגירת המוחלפים ברשימה ({replaced.length})
                        </button>
                      )}
                  </>
                )}
                {cleanupMessage && <p role="status">{cleanupMessage}</p>}
              </CompactRow>
            )}
            {section === "tonight" && (
              <CompactRow id="last-night" title="תוצאת הריצה האחרונה">
                {last
                  ? (
                    <>
                      <p>
                        {new Date(last.run_at).toLocaleString("he-IL", {
                          timeZone: "Asia/Jerusalem",
                        })} · {last.results.filter((r) =>
                          r.outcome === "merged"
                        ).length} מוזגו
                      </p>
                      {last.results.map((r, i) => (
                        <p key={i}>
                          #{r.number}: {r.reason || r.outcome}
                        </p>
                      ))}
                    </>
                  )
                  : <p>אין תוצאת ריצה מאומתת להצגה.</p>}
                <Link to="/releases?legacy=1&tab=history">
                  היסטוריה ובקרת שחרורים
                </Link>
              </CompactRow>
            )}
          </section>
        )}
      {showTop && (
        <button className="rc-to-top" onClick={top}>↑ חזרה למעלה</button>
      )}
    </main>
  );
}

function SqlCard(
  { row, owner, linkedPr, refresh }: {
    row: SqlRow;
    refresh: () => Promise<void>;
    owner: boolean;
    linkedPr?: ReleasePr;
  },
) {
  const canAct = owner && !releasesApi.workflowReadOnly && !isQueueHistory(row) &&
    (row.database !== "admin" || !adminMigrationQueueApi.readOnly);
  const [action, setAction] = useState<"approve" | "hold" | "release" | "cancel" | null>(null);
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const inFlight = useRef(false);
  useEffect(() => { setAction(null); setConfirmed(false); setSql(null); },
    [row.id, row.status, row.held, row.emergency, row.sql_sha256]);
  const actionable = ["pending", "approved"].includes(row.status);
  const labels = { approve: "אישור", hold: "השהיה", release: "שחרור השהיה", cancel: "ביטול" };
  async function refreshState() {
    try { await refresh(); } catch (error) {
      toast.error("רענון מצב התור נכשל: " + queueErrorMessage(error));
    }
  }
  async function perform(next:'approve'|'hold'|'release'|'cancel',why:string){
    if(!canAct||!actionable||!why.trim()||inFlight.current||next==='approve'&&(!row.emergency||row.status!=='pending'))return
    inFlight.current=true;setBusy(true);setMessage('')
    try{
      const api=row.database==='admin'?adminMigrationQueueApi:migrationQueueApi
      await api.action(row.id,next,why.trim())
      const text=next==='approve'?'האישור נרשם. השינוי ירוץ בדקות הקרובות; מצב החלה יופיע בכרטיס.':next==='hold'?'השינוי הושהה.':next==='release'?'ההשהיה שוחררה.':'השינוי בוטל.'
      setMessage(text);toast.success(text);setAction(null);setReason('');setConfirmed(false)
    }catch(error){const text='הפעולה לא אושרה: '+queueErrorMessage(error);setMessage(text);toast.error(text)}
    finally{await refreshState();inFlight.current=false;setBusy(false)}
  }
  async function submit(event:React.FormEvent){event.preventDefault();if(action&&confirmed&&reason.trim())await perform(action,reason)}
  const [sql, setSql] = useState<string | null>(null),
    [note, setNote] = useState(""),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false);
  async function view() {
    if (inFlight.current) return;
    inFlight.current = true; setSql(null); setMessage("");
    setBusy(true);
    try {
      const content = row.database === "jumpstart"
        ? (await migrationQueueApi.state(row.id, true)).items.find((item) =>
          item.id === row.id
        )?.sql
        : (await releasesApi.centerSql(row.database, row.id)).sql;
      if (typeof content !== "string") {
        throw Error("תוכן SQL אינו זמין בקריאה המאומתת.");
      }
      const hash = [
        ...new Uint8Array(
          await crypto.subtle.digest(
            "SHA-256",
            new TextEncoder().encode(content),
          ),
        ),
      ].map((n) => n.toString(16).padStart(2, "0")).join("");
      if (row.sql_sha256 && hash !== row.sql_sha256) {
        throw Error("תוכן SQL השתנה מאז החיפוש; יש לרענן לפני הצגה.");
      }
      setSql(content); toast.success("תוכן SQL נטען ואומת בהצלחה.");
    } catch (e) {
      const text = "הצגת SQL נכשלה: " + queueErrorMessage(e);
      setMessage(text); toast.error(text);
    } finally {
      await refreshState(); inFlight.current = false; setBusy(false);
    }
  }
  async function comment() {
    if (inFlight.current || !canAct || note.trim().length < 5) return;
    inFlight.current = true; setBusy(true);
    try {
      setMessage(
        (await releasesApi.centerSqlComment(row.database, row.id, note))
          .message,
      );
      setNote(""); toast.success("ההערה נשמרה בהצלחה.");
    } catch (e) {
      const text = "שמירת ההערה נכשלה: " + queueErrorMessage(e);
      setMessage(text); toast.error(text);
    } finally {
      await refreshState(); inFlight.current = false; setBusy(false);
    }
  }
  return (
    <CompactRow
      id={"sql:" + row.database + row.id}
      title={row.file_path?.split("/").pop() ?? row.name}
      status={queueStatusLine(row)}
      subtitle={
        <>
          <span>תור {row.database === "admin" ? "Admin" : "JumpStart"}</span>
          {row.pr_url && (
            <>
              · <bdi>PR #{parsePrSearch(row.pr_url)?.number}</bdi>
            </>
          )}
          {(row.task_title ?? linkedPr?.task?.title) && (
            <>· {row.task_title ?? linkedPr?.task?.title}</>
          )}
        </>
      }
    >
      <small>
        SQL · מסד {row.database === "admin" ? "Admin" : "JumpStart"} ·{" "}
        {queueStatusLine(row)} · {row.emergency ? "חירום" : "רגיל"}{row.held ? " · מושהה" : ""}
      </small>
      <h3>
        <bdi>{row.name}</bdi>
      </h3>
      <div className="rc-sql-meta">
      <p>
        <bdi>{row.version}</bdi>
      </p>
      {row.sql_sha256 && <code>{row.sql_sha256}</code>}
      {row.task_id && (
        <a
          href={`https://hub.jumpstart.co.il/tasks/table?task=${
            encodeURIComponent(row.task_id)
          }`}
          target="_blank"
          rel="noreferrer"
        >
          משימה: {row.task_title ?? row.task_id}
        </a>
      )}
      {row.database === "admin" && /^[a-f0-9]{40}$/i.test(row.git_sha ?? "") &&
        row.file_path?.startsWith("supabase/migrations/") && (
        <a
          className="rc-button"
          href={`https://github.com/the-Otomator/otomator-admin/blob/${row.git_sha}/${
            row.file_path.split("/").map(encodeURIComponent).join("/")
          }`}
          target="_blank"
          rel="noreferrer"
        >
          לצפות בקובץ SQL מהגרסה שבתור
        </a>
      )}
      {row.pr_url && (
        <a href={row.pr_url} target="_blank" rel="noreferrer">
          ל־PR המקושר
        </a>
      )}
      </div>
      <div className="rc-sql-primary">
      {row.status === "pending" && !row.emergency && <p className="rc-sql-info">ירוץ אוטומטית בחלון השחרור הבא (לילה/סוף שבוע)</p>}
      <div className="rc-actions">
        {row.status === "pending" && row.emergency && <button className="rc-sql-approve" disabled={!canAct || busy} onClick={() => void perform("approve","אישור חירום מפורש בכרטיס המיגרציה")}>לאשר ולהריץ SQL בחירום</button>}
        <button disabled={busy} onClick={() => void view()}>הצגת SQL</button>
        {actionable && <>
          <button disabled={!canAct || busy} onClick={() => { setAction(row.held ? "release" : "hold"); setConfirmed(false); }}>{row.held ? "שחרור השהיה" : "השהיה"}</button>
          <button disabled={!canAct || busy} onClick={() => { setAction("cancel"); setConfirmed(false); }}>ביטול</button>
        </>}
      </div>
      {row.database === "admin" && row.status === "pending" && !row.emergency && !row.held && !row.pr_url && !row.workflow_approval_id && (
        <WorkflowOwnerControl owner={owner} readOnly={busy || adminMigrationQueueApi.readOnly}
          label="לסמן כחירום ולאשר עכשיו" initialMode="emergency"
          sql={{database:"admin",item_id:row.id,sha256:row.sql_sha256 ?? "",name:row.name}}
          onFinished={async (result, error) => {
            const text = result ? ownerApprovalFeedback(result) : "הפעולה נכשלה: " + queueErrorMessage(error);
            setMessage(text);
            if (result?.approved) toast.success(text); else toast.error(text);
            await refreshState();
          }} />
      )}
      {action && <form className="rc-sql-confirm" onSubmit={(event) => void submit(event)}>
        <strong>{labels[action]}: <bdi>{row.name}</bdi></strong>
        <label>סיבה<input value={reason} onChange={(event) => { setReason(event.target.value); setConfirmed(false); }} required maxLength={2000} /></label>
        <label className="rc-sql-check"><input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />
          {action === "approve" ? "אני מאשר להריץ את השינוי הזה עכשיו, מחוץ לחלון הלילה." : `אני מאשר ${labels[action]} של שינוי זה.`}
        </label>
        <div className="rc-actions"><button className={action === "approve" ? "rc-sql-approve" : ""} disabled={!canAct || busy || !reason.trim() || !confirmed} type="submit">{busy ? "שולח…" : action === "approve" ? "לאשר ולהריץ" : `אישור ${labels[action]}`}</button>
          <button type="button" disabled={busy} onClick={() => { setAction(null); setConfirmed(false); }}>חזרה</button></div>
      </form>}
      </div>
      {sql !== null && <pre dir="ltr">{sql}</pre>}
      {!isQueueHistory(row) && (
        <>
          <label>
            הערה על SQL
            <textarea
              value={note}
              maxLength={2000}
              onChange={(e) => setNote(e.target.value)}
            />
          </label>
          <button
            disabled={!canAct || busy || note.trim().length < 5}
            onClick={() => void comment()}
          >
            להגיב
          </button>
        </>
      )}
      <Link
        to={`/releases?legacy=1&tab=history&search=${
          encodeURIComponent(row.id)
        }`}
      >
        הערות והיסטוריית SQL
      </Link>
      {message && <p role="status">{message}</p>}
    </CompactRow>
  );
}
